import { io, Socket } from 'socket.io-client';
import type {
  QueueStatsResponse,
  ClassifierStatsResponse,
  RateLimitResponse,
  IconectStatsResponse,
  OneDriveStatsResponse,
  PhiStatsResponse,
  HealthResponse,
  K8sWorkersResponse,
  K8sVllmResponse,
} from '../types';

// WebSocket events for real-time updates
// Note: Document stats and classifier errors use HTTP polling (they support batch filtering)
export interface SocketEvents {
  'stats:queue': (data: QueueStatsResponse) => void;
  'stats:classifier': (data: ClassifierStatsResponse) => void;
  'stats:rateLimits': (data: RateLimitResponse) => void;
  'stats:iconect': (data: IconectStatsResponse) => void;
  'stats:oneDrive': (data: OneDriveStatsResponse) => void;
  'stats:phi': (data: PhiStatsResponse) => void;
  'stats:k8sWorkers': (data: K8sWorkersResponse) => void;
  'stats:k8sVllm': (data: K8sVllmResponse) => void;
  'health': (data: HealthResponse) => void;
  'job:completed': (data: { queue: string; documentId: string; duration: number }) => void;
  'job:failed': (data: { queue: string; documentId: string; error: string }) => void;
  'export:progress': (data: { exportId: string; processedDocuments: number; totalDocuments: number; rowCount: number }) => void;
  'export:complete': (data: { exportId: string; fileName: string; documentCount: number; rowCount: number; sizeBytes: number }) => void;
  'export:failed': (data: { exportId: string; error: string }) => void;
  'search:progress': (data: { searchId: string; processedDocuments: number; totalDocuments: number; matchCount: number; cappedAt: number | null }) => void;
  'search:complete': (data: { searchId: string; matchCount: number; processedDocuments: number; totalDocuments: number; cappedAt: number | null; savedId?: string | null; autoSaveError?: string | null; durationMs?: number }) => void;
  'search:failed': (data: { searchId: string; error: string }) => void;
  'search:paused': (data: { searchId: string; matchCount?: number; processedDocuments?: number; totalDocuments?: number; cappedAt?: number | null; durationMs?: number }) => void;
  'search:cancelled': (data: { searchId: string; matchCount?: number; processedDocuments?: number; totalDocuments?: number; cappedAt?: number | null; durationMs?: number }) => void;
  'vectara:progress': (data: { exportId: string; status: string; total: number; processed: number; succeeded: number; failed: number; skipped: number; durationMs?: number }) => void;
  'vectara:complete': (data: { exportId: string; status: string; total: number; processed: number; succeeded: number; failed: number; skipped: number; failures: { documentId: string; error: string }[]; skips?: { documentId: string; reason: string }[]; durationMs?: number }) => void;
  'vectara:failed': (data: { exportId: string; error: string }) => void;
  'vectara:cancelled': (data: { exportId: string; status: string; total?: number; processed?: number; succeeded?: number; failed?: number; skipped?: number; durationMs?: number }) => void;
}

class SocketManager {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<Function>> = new Map();

  connect(): Socket {
    // Return existing socket if it exists (even if still connecting)
    // This prevents multiple hooks from creating duplicate sockets
    if (this.socket) {
      return this.socket;
    }

    // Connect to current origin (works for both dev proxy and production).
    // The hc_token cookie is sent automatically when withCredentials is true,
    // and the dashboard server's io.use() handshake reads it.
    this.socket = io(window.location.origin, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      withCredentials: true,
    });

    this.socket.on('connect', () => {
      console.log('[Socket] Connected, id:', this.socket?.id);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error.message);
    });

    // Debug: Log all incoming events in development
    if (import.meta.env.DEV) {
      this.socket.onAny((eventName, data) => {
        console.log(`[Socket] Event: ${eventName}`, data?.timestamp || '');
      });
    }

    // Note: Socket.io automatically persists listeners across reconnections,
    // so we don't need to re-register them manually.

    return this.socket;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  on<K extends keyof SocketEvents>(event: K, callback: SocketEvents[K]): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    if (this.socket) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.socket.on(event, callback as any);
    }
  }

  off<K extends keyof SocketEvents>(event: K, callback: SocketEvents[K]): void {
    this.listeners.get(event)?.delete(callback);

    if (this.socket) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.socket.off(event, callback as any);
    }
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  // Reconnect with new API key
  reconnect(): void {
    this.disconnect();
    this.listeners.clear();
    this.connect();
  }
}

export const socketManager = new SocketManager();

// Expose for debugging in browser console
if (typeof window !== 'undefined') {
  (window as unknown as { __SOCKET_DEBUG__: SocketManager }).__SOCKET_DEBUG__ = socketManager;
}
